import { physicalProducts } from '../../../common/utils/cartProductUtils.js';
import { DEFAULT_MAX_ALLOWED_ITEM_QUANTITY_FOR_ORDER } from '../../../common/utils/validationUtils.js';
import { StockAvailabilityStatus } from '../../../generated/api/stockAvailabilityStatus';
import {
  calculateMonthlyAndOneTimeChargesTotals,
  getOfferForBasketItem,
  getOnlineModelForBasketItem,
} from '../../ShoppingBasket/shoppingBasketUtils.js';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasket.js';
import type { Offer } from '../../../generated/api/offer.js';
import type { OnlineModel } from '../../../generated/api/onlineModel.js';
import type { PlaceOrderFormData } from '../placeOrderTypes.js';

// ============================================================================
// Session Storage for Card Payment Flow
// ============================================================================

/**
 * Session storage key for card payment data.
 * Stores form data needed for thank you page after returning from Nets.
 */
export const PLACE_ORDER_FORM_DATA_SESSION_KEY = 'delivery-details-for-place-order-card-payment';

/**
 * Data stored in session storage during card payment flow.
 */
export interface StoredPlaceOrderCardPaymentData {
  /** Full form data for thank you page */
  formData: PlaceOrderFormData;
  /** Transaction ID from initialize response */
  transactionId: string;
  /** Order ID from initialize response - needed for finalize API */
  orderId: string;
  /** Timestamp for expiry/cleanup */
  timestamp: number;
}

/**
 * Save card payment form data to session storage before redirecting to Nets.
 */
export const saveFormDataToSession = (data: StoredPlaceOrderCardPaymentData): void => {
  sessionStorage.setItem(PLACE_ORDER_FORM_DATA_SESSION_KEY, JSON.stringify(data));
};

/**
 * Retrieve card payment form data from session storage after returning from Nets.
 * Returns null if data is not found or cannot be parsed.
 */
export const getFormDataFromSession = (): StoredPlaceOrderCardPaymentData | null => {
  const stored = sessionStorage.getItem(PLACE_ORDER_FORM_DATA_SESSION_KEY);
  if (!stored) {
    return null;
  }
  try {
    return JSON.parse(stored) as StoredPlaceOrderCardPaymentData;
  } catch {
    return null;
  }
};

/**
 * Clear card payment form data from session storage.
 * Should be called on thank you page after reading the data.
 */
export const clearFormDataFromSession = (): void => {
  sessionStorage.removeItem(PLACE_ORDER_FORM_DATA_SESSION_KEY);
};

// ============================================================================
// Card Payment Eligibility
// ============================================================================

type PhysicalCategory = (typeof physicalProducts)[number];

const isPhysicalCategory = (category: string | undefined): category is PhysicalCategory =>
  !!category && (physicalProducts as ReadonlyArray<string>).includes(category);

const isModelPhysical = (model?: OnlineModel): boolean => isPhysicalCategory(model?.category);

const areAllItemsPhysical = (basket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  return (basket.items || []).every(item => isModelPhysical(getOnlineModelForBasketItem(onlineModels, item)));
};

const hasValidTotalPrices = (
  basket: ShoppingBasketType,
  onlineModels: OnlineModel[],
  deliveryCharge: number
): boolean => {
  const { monthlyRecurringCharges, onetimeCharges } = calculateMonthlyAndOneTimeChargesTotals(
    onlineModels,
    basket.items
  );
  return onetimeCharges + deliveryCharge > 0 && monthlyRecurringCharges === 0;
};

interface StockInfo {
  status?: string;
  rangeLower?: number;
  rangeUpper?: number;
}

const extractStockInfo = (offer?: Offer): StockInfo => {
  const status = offer?.stockAvailabilityStatus as string | undefined;
  const range = offer?.stockAvailabilityRange as { lower?: number; upper?: number } | undefined;
  return {
    status,
    rangeLower: range?.lower,
    rangeUpper: range?.upper,
  };
};

const getMaxQuantityAllowed = (info: StockInfo): number => {
  if (!info.status || info.rangeLower == null) {
    return 0;
  }
  if (info.status === StockAvailabilityStatus.SHIPMENTS_ARRIVING) {
    return info.rangeUpper != null ? info.rangeUpper : DEFAULT_MAX_ALLOWED_ITEM_QUANTITY_FOR_ORDER;
  }
  if (info.status === StockAvailabilityStatus.NO_SHIPMENTS_ARRIVING) {
    return info.rangeLower;
  }
  // Unknown status -> invalid
  return 0;
};

const isStockValid = (basket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  return (basket.items || []).every(item => {
    const offer = getOfferForBasketItem(onlineModels, item);
    const stockInfo = extractStockInfo(offer);
    const maxAllowed = getMaxQuantityAllowed(stockInfo);
    return item.quantity <= maxAllowed;
  });
};

const areAllCommercialProductsPrepaidAllowed = (basket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  return (basket.items || []).every(item => {
    const offer = getOfferForBasketItem(onlineModels, item);
    const products = offer?.commercialProducts || [];
    const matched = products.filter(p => p.commercialProductCode === item.offer.commercialProductGuid);
    if (!matched.length) {
      return false; // Missing commercial product data invalidates
    }
    return matched.every(p => {
      const prepaid = p.prepaidPaymentAllowed as boolean | undefined;
      return prepaid === true; // Undefined or false invalidates
    });
  });
};

export const isCardPaymentPossible = (
  shoppingBasket: ShoppingBasketType,
  onlineModels: OnlineModel[],
  deliveryCharge: number
): boolean => {
  const physicalOk = areAllItemsPhysical(shoppingBasket, onlineModels);
  if (!physicalOk) {
    return false;
  }
  const totalPricesOk = hasValidTotalPrices(shoppingBasket, onlineModels, deliveryCharge);
  if (!totalPricesOk) {
    return false;
  }
  const stockOk = isStockValid(shoppingBasket, onlineModels);
  if (!stockOk) {
    return false;
  }
  return areAllCommercialProductsPrepaidAllowed(shoppingBasket, onlineModels);
};
